import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, Save, Trash2, Languages, ArrowLeft, Pencil, Link2, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Admin · Perguntas (Pacote-based)
 *
 * Vista 1 — Lista de PACOTES importados (por defeito):
 *   colunas: nome do ficheiro, idioma, N.º perguntas, jogos associados (chips),
 *   ações: [Editar perguntas] [Associar/Desassociar jogo] [Renomear] [Eliminar].
 *
 * Vista 2 — Detalhe (perguntas de um pacote):
 *   tabela editável idêntica à antiga; alterações refletem-se em TODOS os jogos
 *   onde o pacote está associado (semântica partilhada — opção b).
 *
 * Import: cria um pacote NOVO (nome = ficheiro Excel). Aceita `game_id` opcional
 * para associar imediatamente; se não for fornecido, o pacote fica no inventário.
 */
export default function AdminQuestions() {
  const [view, setView] = useState("packs"); // "packs" | "detail"
  const [detailPack, setDetailPack] = useState(null);

  const openPackDetail = (pack) => { setDetailPack(pack); setView("detail"); };
  const backToList = () => { setDetailPack(null); setView("packs"); };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2">
        {view === "detail" && (
          <Button variant="outline" size="sm" onClick={backToList} data-testid="btn-back-to-packs">
            <ArrowLeft className="w-4 h-4 mr-1" /> Pacotes
          </Button>
        )}
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">
          {view === "packs" ? "Perguntas · Pacotes" : `Perguntas · ${detailPack?.name}`}
        </h1>
      </div>

      {view === "packs" ? (
        <PacksList onOpen={openPackDetail} />
      ) : (
        <PackDetail pack={detailPack} onBack={backToList} />
      )}
    </div>
  );
}

/* ============================================================ */
/*  Lista de pacotes                                             */
/* ============================================================ */
function PacksList({ onOpen }) {
  const [packs, setPacks] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignPack, setAssignPack] = useState(null);
  const [renamePack, setRenamePack] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, g] = await Promise.all([
        api.get("/question-packs"),
        api.get("/games"),
      ]);
      setPacks(p.data || []);
      setGames(g.data || []);
    } catch (e) {
      toast.error("Falhou a carregar pacotes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const deletePack = async (pack) => {
    if (!window.confirm(`Eliminar o pacote "${pack.name}" e as suas ${pack.question_count} perguntas? Esta ação também remove as perguntas de TODOS os jogos onde o pacote está associado.`)) return;
    try {
      await api.delete(`/question-packs/${pack.id}`);
      toast.success("Pacote eliminado");
      load();
    } catch (e) {
      toast.error("Falhou a eliminar");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Button data-testid="btn-open-import" onClick={() => setImportOpen(true)} className="bg-violet-600 hover:bg-violet-700">
          <Upload className="w-4 h-4 mr-1" /> Importar Excel
        </Button>
        <div className="text-xs text-slate-500 ml-2">{packs.length} pacote(s)</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="packs-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase font-bold">Nome do pacote</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold w-24">Idioma</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold w-24">Perguntas</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Jogos associados</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold w-32">Importado</th>
              <th className="text-right px-3 py-3 text-xs uppercase font-bold w-72">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">A carregar…</td></tr>
            )}
            {!loading && packs.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">
                Sem pacotes ainda. Clica em <b>Importar Excel</b> para criar o primeiro.
              </td></tr>
            )}
            {!loading && packs.map((p) => (
              <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`pack-row-${p.id}`}>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onOpen(p)}
                    className="text-left font-semibold text-slate-900 hover:text-violet-700"
                    data-testid={`btn-open-pack-${p.id}`}
                  >
                    {p.name}
                  </button>
                  {p.source_filename && p.source_filename !== p.name && (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Ficheiro: {p.source_filename}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                    p.language === "pt" ? "bg-emerald-50 text-emerald-700" :
                    p.language === "en" ? "bg-sky-50 text-sky-700" :
                    "bg-amber-50 text-amber-700"
                  }`}>
                    {p.language.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-3 tabular-nums font-bold text-slate-700">{p.question_count}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(p.games || []).length === 0 ? (
                      <span className="text-xs text-slate-400 italic">Sem jogo associado</span>
                    ) : p.games.map((g) => (
                      <span key={g.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-50 text-violet-700 border border-violet-200">
                        {g.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
                  {p.created_at ? new Date(p.created_at).toLocaleDateString("pt-PT") : "—"}
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-1 justify-end flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => onOpen(p)} data-testid={`btn-edit-pack-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAssignPack(p)} data-testid={`btn-assign-pack-${p.id}`}>
                      <Link2 className="w-3.5 h-3.5 mr-1" /> Jogos
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRenamePack(p)} data-testid={`btn-rename-pack-${p.id}`}>
                      <Save className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deletePack(p)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`btn-delete-pack-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        games={games}
        onDone={() => { setImportOpen(false); load(); }}
      />
      <AssignDialog
        pack={assignPack}
        games={games}
        onClose={() => setAssignPack(null)}
        onSaved={() => { setAssignPack(null); load(); }}
      />
      <RenameDialog
        pack={renamePack}
        onClose={() => setRenamePack(null)}
        onSaved={() => { setRenamePack(null); load(); }}
      />
    </div>
  );
}

/* ============================================================ */
/*  Dialog · Importar Excel                                      */
/* ============================================================ */
function ImportDialog({ open, onClose, games, onDone }) {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("pt");
  const [gameId, setGameId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setLanguage("pt"); setGameId(""); } }, [open]);

  const submit = async () => {
    if (!file) { toast.error("Escolhe um ficheiro Excel"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("language", language);
      if (gameId) fd.append("game_id", gameId);
      const res = await api.post("/question-packs/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Pacote criado com ${res.data.imported} perguntas em ${language.toUpperCase()}`);
      if (res.data.rejected_invalid_position > 0) {
        toast.warning(`${res.data.rejected_invalid_position} linha(s) ignorada(s) por posição inválida`);
      }
      const incomplete = res.data.incomplete_cards || [];
      if (incomplete.length > 0) {
        const shown = incomplete.slice(0, 5).map((c) => `Carta ${c.card_number}: falta ${c.missing.join(", ")}`).join(" · ");
        const rest = incomplete.length > 5 ? ` (+${incomplete.length - 5} cartas)` : "";
        toast.warning(`${incomplete.length} carta(s) incompleta(s) — ${shown}${rest}`, { duration: 15000 });
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falhou a importação");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Importar pacote de perguntas</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Ficheiro Excel (.xlsx)</Label>
            <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file"/>
          </div>
          <div>
            <Label className="text-xs">Idioma do pacote</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="import-lang"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">🇵🇹 Português (PT)</SelectItem>
                <SelectItem value="en">🇬🇧 English (EN)</SelectItem>
                <SelectItem value="es">🇪🇸 Español (ES)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Associar de imediato a um jogo (opcional)</Label>
            <Select value={gameId || "__none__"} onValueChange={(v) => setGameId(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="import-game"><SelectValue placeholder="Sem jogo"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sem jogo — só no inventário</SelectItem>
                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1 italic">
              Podes associar/desassociar a mais jogos depois, no botão <b>Jogos</b> de cada pacote.
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !file} data-testid="btn-submit-import" className="bg-violet-600 hover:bg-violet-700">
            {busy ? "A importar..." : "Importar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================ */
/*  Dialog · Associar / Desassociar jogos                        */
/* ============================================================ */
function AssignDialog({ pack, games, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (pack) setSelected(new Set((pack.games || []).map((g) => g.id)));
  }, [pack]);
  if (!pack) return null;

  const toggle = (gid) => {
    setSelected((s) => { const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });
  };

  const save = async () => {
    setBusy(true);
    try {
      const current = new Set((pack.games || []).map((g) => g.id));
      const toAdd = [...selected].filter((g) => !current.has(g));
      const toRemove = [...current].filter((g) => !selected.has(g));
      for (const gid of toAdd) {
        await api.post(`/question-packs/${pack.id}/assign`, { game_id: gid });
      }
      for (const gid of toRemove) {
        await api.post(`/question-packs/${pack.id}/unassign`, { game_id: gid });
      }
      toast.success("Associações atualizadas");
      onSaved();
    } catch (e) {
      toast.error("Falhou a atualização");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!pack} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Jogos associados · {pack.name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-3">
          As perguntas deste pacote passam a estar disponíveis nos jogos assinalados.
          Editar uma pergunta aqui afeta <b>todos</b> os jogos onde o pacote está associado.
        </div>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {games.length === 0 && <div className="text-slate-400 text-sm">Sem jogos configurados.</div>}
          {games.map((g) => (
            <label key={g.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border-2 transition ${
              selected.has(g.id) ? "bg-violet-50 border-violet-300" : "border-transparent hover:bg-slate-50"
            }`}>
              <input
                type="checkbox"
                checked={selected.has(g.id)}
                onChange={() => toggle(g.id)}
                data-testid={`assign-game-${g.id}`}
                className="w-4 h-4"
              />
              <span className="font-medium">{g.name}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">
                {g.status || "—"}
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={save} disabled={busy} data-testid="btn-save-assign" className="bg-violet-600 hover:bg-violet-700">
            {busy ? "A guardar..." : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================ */
/*  Dialog · Renomear pacote                                     */
/* ============================================================ */
function RenameDialog({ pack, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (pack) setName(pack.name || ""); }, [pack]);
  if (!pack) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/question-packs/${pack.id}`, { name: name.trim() });
      toast.success("Pacote renomeado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falhou renomear");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!pack} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Renomear pacote</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="rename-input" autoFocus/>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={save} disabled={busy || !name.trim()} data-testid="btn-save-rename">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================ */
/*  Detalhe de um pacote (edição de perguntas)                   */
/* ============================================================ */
function PackDetail({ pack, onBack }) {
  const [questions, setQuestions] = useState([]);
  const [search, setSearch] = useState("");
  const [filterPos, setFilterPos] = useState("__all__");
  const [selected, setSelected] = useState(new Set());
  const [translateQ, setTranslateQ] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!pack?.id) return;
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterPos && filterPos !== "__all__") params.position = filterPos;
      const r = await api.get(`/question-packs/${pack.id}/questions`, { params });
      setQuestions(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, [pack?.id, search, filterPos]);

  const updateField = (i, field, value) => {
    const arr = [...questions]; arr[i] = { ...arr[i], [field]: value }; setQuestions(arr);
  };
  const saveOne = async (q) => {
    await api.put(`/questions/${q.id}`, {
      position: q.position, question: q.question,
      option_1: q.option_1, option_2: q.option_2, option_3: q.option_3, option_4: q.option_4,
      correct: q.correct, card_number: q.card_number ?? null,
      translations: q.translations || {},
    });
    toast.success("Guardado");
  };
  const deleteOne = async (q) => {
    if (!window.confirm("Eliminar esta pergunta?")) return;
    await api.delete(`/questions/${q.id}`);
    setQuestions(questions.filter((x) => x.id !== q.id));
  };
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === questions.length ? new Set() : new Set(questions.map((q) => q.id)));
  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Eliminar ${selected.size} pergunta(s)?`)) return;
    await api.post("/questions/bulk-delete", { ids: [...selected] });
    toast.success("Eliminadas");
    setSelected(new Set()); load();
  };
  const downloadExport = async () => {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const res = await fetch(`${API}/question-packs/${pack.id}/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${pack.name}.xlsx`; a.click();
  };

  return (
    <div>
      <div className="rounded-2xl border-2 border-violet-100 bg-violet-50/30 p-3 mb-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-white border">
          <Languages className="w-3 h-3"/> {pack?.language?.toUpperCase()}
        </span>
        <span className="text-slate-600">
          Associado a: {(pack?.games || []).length === 0
            ? <em className="text-slate-400">nenhum jogo</em>
            : pack.games.map((g) => g.name).join(", ")}
        </span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadExport} data-testid="btn-export-pack">
            <Download className="w-4 h-4 mr-1"/> Exportar Excel
          </Button>
        </div>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div className="max-w-xs flex-1 min-w-[220px]">
          <Label className="text-xs">Pesquisar</Label>
          <Input placeholder="pergunta ou opção..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pack-search"/>
        </div>
        <div className="w-48">
          <Label className="text-xs">Posição</Label>
          <Select value={filterPos} onValueChange={setFilterPos}>
            <SelectTrigger data-testid="pack-pos-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas</SelectItem>
              {"ABCDEFGHIJ".split("").map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 pb-2">{questions.length} resultado(s)</div>
        <div className="ml-auto pb-1">
          <Button
            disabled={!selected.size} onClick={deleteSelected} variant="outline"
            className="text-rose-700 border-rose-300 hover:bg-rose-50 disabled:opacity-50"
            data-testid="btn-delete-selected"
          >
            <Trash2 className="w-4 h-4 mr-1"/> Eliminar selecionadas ({selected.size})
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" checked={questions.length > 0 && selected.size === questions.length} onChange={toggleAll} className="w-4 h-4 cursor-pointer"/>
              </th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold w-16">Carta</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold w-12">Pos</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Pergunta</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Op1</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Op2</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Op3</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold">Op4</th>
              <th className="text-left px-3 py-3 text-xs uppercase font-bold w-20">Correta</th>
              <th className="px-3 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-8 text-slate-400">A carregar…</td></tr>}
            {!loading && questions.map((q, i) => (
              <tr key={q.id} className={`border-b ${selected.has(q.id) ? "bg-rose-50" : ""}`}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={selected.has(q.id)} onChange={() => toggleSelect(q.id)} className="w-4 h-4 cursor-pointer"/>
                </td>
                <td className="px-2 py-2"><Input className="w-14" type="number" min={1} value={q.card_number ?? ""} placeholder="—" onChange={(e) => updateField(i, "card_number", e.target.value === "" ? null : parseInt(e.target.value, 10))} data-testid={`q-card-${i}`}/></td>
                <td className="px-2 py-2"><Input className="w-14" value={q.position} onChange={(e) => updateField(i, "position", e.target.value.toUpperCase())}/></td>
                <td className="px-2 py-2"><Input value={q.question} onChange={(e) => updateField(i, "question", e.target.value)}/></td>
                <td className="px-2 py-2"><Input value={q.option_1} onChange={(e) => updateField(i, "option_1", e.target.value)}/></td>
                <td className="px-2 py-2"><Input value={q.option_2} onChange={(e) => updateField(i, "option_2", e.target.value)}/></td>
                <td className="px-2 py-2"><Input value={q.option_3} onChange={(e) => updateField(i, "option_3", e.target.value)}/></td>
                <td className="px-2 py-2"><Input value={q.option_4} onChange={(e) => updateField(i, "option_4", e.target.value)}/></td>
                <td className="px-2 py-2"><Input type="number" min={1} max={4} className="w-16" value={q.correct} onChange={(e) => updateField(i, "correct", +e.target.value)}/></td>
                <td className="px-2 py-2 flex gap-1">
                  <button onClick={() => saveOne(q)} className="text-emerald-600 p-1" title="Guardar"><Save className="w-4 h-4"/></button>
                  <button onClick={() => setTranslateQ(q)} className="text-sky-600 p-1" title="Traduções"><Languages className="w-4 h-4"/></button>
                  <button onClick={() => deleteOne(q)} className="text-rose-600 p-1" title="Eliminar"><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {!loading && !questions.length && (
              <tr><td colSpan={10} className="text-center py-6 text-slate-400">Sem perguntas neste pacote.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <TranslationsDialog q={translateQ} onClose={() => setTranslateQ(null)} onSaved={() => { setTranslateQ(null); load(); }}/>
    </div>
  );
}

/* ============================================================ */
/*  Diálogo de traduções (compat com fluxo antigo)               */
/* ============================================================ */
function TranslationsDialog({ q, onClose, onSaved }) {
  const [tr, setTr] = useState({ en: {}, es: {} });
  useEffect(() => {
    if (q) setTr({ en: q.translations?.en || {}, es: q.translations?.es || {} });
  }, [q]);
  if (!q) return null;
  const setField = (langKey, field, value) => setTr((prev) => ({ ...prev, [langKey]: { ...prev[langKey], [field]: value } }));
  const save = async () => {
    await api.put(`/questions/${q.id}`, { translations: tr });
    toast.success("Traduções guardadas");
    onSaved();
  };
  return (
    <Dialog open={!!q} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Traduções · Posição {q.position}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 mb-2">Original (PT): <strong>{q.question}</strong></div>
        <div className="grid grid-cols-2 gap-4">
          {["en", "es"].map((lang) => (
            <div key={lang} className="bg-slate-50 p-3 rounded-lg space-y-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{lang}</div>
              <Input placeholder="Pergunta" value={tr[lang]?.question || ""} onChange={(e) => setField(lang, "question", e.target.value)}/>
              {[1,2,3,4].map((n) => (
                <Input key={n} placeholder={`Opção ${n}`} value={tr[lang]?.[`option_${n}`] || ""} onChange={(e) => setField(lang, `option_${n}`, e.target.value)}/>
              ))}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save}>Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
